// hydration.effects.ts
import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core'
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects'
import { Action, Store } from '@ngrx/store'
import { switchMap, tap } from 'rxjs/operators'
import * as HydrationActions from './base-hydration.actions'
import { isPlatformBrowser } from '@angular/common'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import localforage from 'localforage'
import { IDBFactory } from 'fake-indexeddb'
import { PromiseUtils } from 'libs/shared/utils/helpers/src/lib/promise-utils'
import { of } from 'rxjs'

@Injectable()
export abstract class BaseHydrationEffects implements OnInitEffects {
  storage!: any
  INDEX_DB_VERSION_KEY = '%indexDBVersion'

  get pk(): string {
    return this.getLocalDBKey()
  }

  constructor(
    protected action$: Actions,
    protected store: Store<{}>,
    @Inject(PLATFORM_ID) protected platformId: InjectionToken<object>,
    @Inject(APP_CONFIG) protected appConfig: any,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // Safely resolve localforage if it bundled weirdly
      const lf = (localforage as any).default || localforage

      this.storage = lf.createInstance({
        name: `galabid_${this.appConfig.app}`,
        storeName: 'app',
        description: 'GalaBid browser cache store',
        driver: [lf.INDEXEDDB, lf.WEBSQL, lf.LOCALSTORAGE],
      })
    } else {
      // Server-side / SSR safe fallback
      this.storage = {
        getItem: async () => null,
        setItem: async () => undefined,
        removeItem: async () => undefined,
        clear: async () => undefined,
        keys: async () => [],
      }
    }
  }

  PRESENTER_URLS: string[] = [
    'localhost',
    'dev-presenter.galabid.com',
    'staging-presenter.galabid.com',
    'presenter.galabid.com',
  ]

  hydrate$ = createEffect(() =>
    this.action$.pipe(
      ofType(HydrationActions.hydrate),
      switchMap(() => {
        
        return this.storage.getItem(this.INDEX_DB_VERSION_KEY).then((version: number) => {
          // check if stored version doesn't match current version
          if (version !== this.appConfig.indexDBVersion) {
            // clear storage
            
            this.storage.clear()
            // store current version number
            this.storage.setItem(this.INDEX_DB_VERSION_KEY, this.appConfig.indexDBVersion)
          }
        })
      }),
      switchMap(() => {
        if (!this.pk) {
          return of(null)
        }
        // getting the state from indexedDB before the hydration
        return Promise.race([
          this.storage.getItem(this.pk),
          // reject if promise does not complete after 5 seconds
          PromiseUtils.timeoutPromise(5000),
        ])
          .then((indexDBState: {}) => indexDBState)
          .catch((error: any) => {
            console.error(error)
            return null
          })
      }),
      switchMap((indexDBState) => {
        const actions: Action[] = []

        if (indexDBState) {
          try {
            const state = JSON.parse(String(indexDBState))
            actions.push(HydrationActions.hydrateSuccess(state))
          } catch (error) {
            actions.push(HydrationActions.hydrateFailure(), HydrationActions.removeState())
          }
        } else {
          actions.push(HydrationActions.noStateHydration())
        }
        return actions
      }),
    ),
  )

  removeState$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(HydrationActions.removeState),
        tap(() => {
          if (isPlatformBrowser(this.platformId)) {
            this.storage.removeItem(this.pk)
            this.storage.removeItem(`${this.pk}_updated`)
          }
        }),
      ),
    { dispatch: false },
  )

  ngrxOnInitEffects(): Action {
    return HydrationActions.hydrate()
  }

  preserveMostRecentStates() {
    const stateKeysDateMap: { [key: string]: Date } = {}

    this.storage
      .keys()
      .then((keys: string[]) => {
        const updatedKeys = keys.filter((key) => key.endsWith('_updated'))

        const datePromises = updatedKeys.map((key) => {
          return this.storage.getItem(key).then((date: string) => {
            stateKeysDateMap[key] = new Date(date)
          })
        })

        return Promise.all(datePromises).then(() => {
          const sortedKeys = Object.keys(stateKeysDateMap).sort(
            (a, b) => stateKeysDateMap[b].getTime() - stateKeysDateMap[a].getTime(),
          )
          const keysToRemove = sortedKeys.slice(10)

          const allKeysToRemove = new Set<string>()

          keysToRemove.forEach((keyToRemove) => {
            allKeysToRemove.add(keyToRemove) // Add the _updated key to remove
            const originalKey = keyToRemove.replace('_updated', '') // Get the original key (without '_updated')
            allKeysToRemove.add(originalKey) // Add the original key to remove
          })

          // Remove the outdated keys and keys_updated
          allKeysToRemove.forEach((keyToRemove) => {
            this.storage.removeItem(keyToRemove)
          })
        })
      })
      .catch((error: any) => {
        console.error('Error in preserving recent states:', error)
      })
  }

  /**
   *Abstract method to be implemented by the extending class
   */
  abstract getLocalDBKey(): string
}
